import React from 'react';
import { Layout } from '../../components/Layout';
import { Checklist } from '../../components/Checklist';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Section } from '../../components/ui/Section';
import { Container } from '../../components/ui/Container';
import { IOS_CHECKLIST } from '../../utils/constants';
import { Smartphone, ShieldCheck, Lock, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function IOS() {
  const breadcrumbs = [{
    label: 'Personal Protocol',
    path: '/personal'
  }, {
    label: 'Device Security'
  }, {
    label: 'iOS Security'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-1921">
      <Section spacing="lg" data-id="element-1922">
        <Container size="lg" data-id="element-1923">
          <div className="mb-12" data-id="element-1924">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-1925">
              <Smartphone className="h-3 w-3 mr-2" data-id="element-1926" />
              Device Library
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-1927">
              iOS Security Protocol
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl" data-id="element-1928">
              Security standards for iPhone & iPad for personal and professional
              use.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1929">
            <div className="lg:col-span-2 space-y-12" data-id="element-1930">
              {/* Baseline */}
              <section data-id="element-1931">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 flex items-center" data-id="element-1932">
                  <ShieldCheck className="h-6 w-6 text-cyan-500 mr-3" data-id="element-1933" />
                  Baseline Security (Mandatory)
                </h2>
                <div className="space-y-4" data-id="element-1934">
                  <Card className="p-5 border-l-4 border-l-cyan-500" data-id="element-1935">
                    <h3 className="font-bold text-slate-100 mb-1" data-id="element-1936">
                      Automatic iOS Updates
                    </h3>
                    <p className="text-slate-400 text-sm" data-id="element-1937">
                      Settings {'>'} General {'>'} Software Update {'>'}{' '}
                      Automatic Updates = ON.
                    </p>
                  </Card>
                  <Card className="p-5 border-l-4 border-l-cyan-500" data-id="element-1938">
                    <h3 className="font-bold text-slate-100 mb-1" data-id="element-1939">
                      Face ID & Passcode
                    </h3>
                    <p className="text-slate-400 text-sm" data-id="element-1940">
                      Use a 6-digit or alphanumeric passcode. Do not use 4
                      digits.
                    </p>
                  </Card>
                  <Card className="p-5 border-l-4 border-l-cyan-500" data-id="element-1941">
                    <h3 className="font-bold text-slate-100 mb-1" data-id="element-1942">
                      Find My & Activation Lock
                    </h3>
                    <p className="text-slate-400 text-sm" data-id="element-1943">
                      Ensures the device cannot be used by others if lost.
                    </p>
                  </Card>
                </div>
              </section>

              {/* Advanced */}
              <section data-id="element-1944">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 flex items-center" data-id="element-1945">
                  <Lock className="h-6 w-6 text-cyan-500 mr-3" data-id="element-1946" />
                  Advanced Protection
                </h2>
                <ul className="space-y-4 list-disc list-inside text-slate-300" data-id="element-1947">
                  <li data-id="element-1948">
                    <strong className="text-slate-100" data-id="element-1949">
                      iCloud Advanced Data Protection:
                    </strong>{' '}
                    Enables end-to-end encryption for iCloud backups. Apple does
                    not hold the keys.
                  </li>
                  <li data-id="element-1950">
                    <strong className="text-slate-100" data-id="element-1951">
                      App Privacy Report:
                    </strong>{' '}
                    Check Settings {'>'} Privacy to see which apps access
                    location/mic secretly.
                  </li>
                  <li data-id="element-1952">
                    <strong className="text-slate-100" data-id="element-1953">Lockdown Mode:</strong>{' '}
                    Only for high-risk targets (journalists/activists).
                  </li>
                </ul>
              </section>

              {/* Common Mistakes */}
              <section className="border-t border-slate-800 pt-8" data-id="element-1954">
                <h2 className="text-xl font-bold text-slate-100 mb-4 text-red-400" data-id="element-1955">
                  Common Mistakes
                </h2>
                <ul className="space-y-3 text-slate-400" data-id="element-1956">
                  <li className="flex items-start" data-id="element-1957">
                    <AlertTriangle className="h-5 w-5 text-red-500/50 mr-3 flex-shrink-0 mt-0.5" data-id="element-1958" />
                    <span data-id="element-1959">Using birthdate as passcode (easy to guess).</span>
                  </li>
                  <li className="flex items-start" data-id="element-1960">
                    <AlertTriangle className="h-5 w-5 text-red-500/50 mr-3 flex-shrink-0 mt-0.5" data-id="element-1961" />
                    <span data-id="element-1962">Allowing message previews on Lock Screen.</span>
                  </li>
                </ul>
              </section>
            </div>

            <div className="space-y-6" data-id="element-1963">
              <Checklist id="ios-checklist" title="iOS Checklist" items={IOS_CHECKLIST} data-id="element-1964" />
              <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-1965">
                <h3 className="font-bold text-slate-100 mb-4" data-id="element-1966">Next Steps</h3>
                <div className="space-y-3" data-id="element-1967">
                  <Link to="/devices/android" data-id="element-1968">
                    <Button variant="secondary" fullWidth className="justify-between group" data-id="element-1969">
                      Android Protocol
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1970" />
                    </Button>
                  </Link>
                  <Link to="/ir/lost-device" data-id="element-1971">
                    <Button variant="secondary" fullWidth className="justify-between group" data-id="element-1972">
                      Lost Device Plan
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1973" />
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
    </Layout>;
}